import {CommonModule} from '@angular/common';
import {Component, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../sdk/src/components/ibase-page/ibase-page.component';
import {CpmCProjectApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {ProjectGeneralInfoComponent} from '../../components/project-general-info/project-general-info.component';
import {ServiceRequestCRUDComponent} from '../../components/service-request/service-request-crud/service-request-crud.component';
import {UserFileCRUDComponent} from '../../components/user-file-crud/user-file-crud.component';
import {ObjectStoreEntryCRUDComponent} from '../../components/object-store-entry-crud/object-store-entry-crud.component';

/**
 * Enum representing available tabs in the project overview page
 */
export enum EProjectTab {
    GENERAL = 'general',
    SERVICE_REQUESTS = 'service-requests',
    FILES = 'files',
    OBJECT_STORE = 'object-store'
}

/**
 * Customer Project Overview Page Component
 *
 * Displays a tabbed interface for viewing and managing project details including:
 * - General information
 * - Service requests
 * - Files
 * - Object store entries
 *
 * The active tab is synchronized with URL query parameters for bookmarkability and navigation.
 */
@Component({
    selector: 'app-cp-overview-page',
    templateUrl: './cp-overview-page.component.html',
    imports: [
        CommonModule,
        PrimeNgImportsModule,
        ProjectGeneralInfoComponent,
        ServiceRequestCRUDComponent,
        UserFileCRUDComponent,
        ObjectStoreEntryCRUDComponent
    ]
})
export class CPOverviewPageComponent extends IBasePageComponent {
    protected readonly EProjectTab = EProjectTab;

    // ===============================================================
    // ===============================================================

    private readonly activatedRoute = inject(ActivatedRoute);

    // ===============================================================
    // ===============================================================

    protected project?: CpmCProjectApiModel;
    /**
     * Subject to trigger statistics refresh in the general info component
     * when data changes in other tabs (service requests, files, object store)
     */
    protected refreshStatistics$ = new Subject<void>();
    /**
     * Gets the currently active tab from the URL query parameters.
     * Defaults to GENERAL tab if no valid tab is specified in the URL.
     *
     * @returns The current active tab
     */
    protected get currentTab(): EProjectTab {
        const tab = this.activatedRoute.snapshot.queryParams['tab'];
        return Object.values(EProjectTab).includes(tab as EProjectTab) ? (tab as EProjectTab) : EProjectTab.GENERAL;
    }
    /**
     * Sets the active tab by updating the URL query parameters.
     * This maintains the tab state in the URL for bookmarking and navigation.
     *
     * @param value - The tab to activate
     */
    protected set currentTab(value: EProjectTab) {
        this.globalsService.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {tab: value},
            queryParamsHandling: 'merge'
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.loadingStr = 'Loading project...';

        await this.globalsService.sessionService.getRouteContextIsReadyPromise();
        this.project = this.globalsService.sessionService.getProject();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // No cleanup required - using snapshot instead of subscriptions
    }
}
